import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { useDialogContentContainer } from '@/components/ui/dialog';
import { OVERLAY_POP_MOTION } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    container?: HTMLElement | null;
  }
>(({ className, align = 'center', sideOffset = 4, collisionPadding, container, ...props }, ref) => (
  <PopoverContentPortal container={container}>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      // 🔴 A POPOVER MAY NEVER TOUCH THE WINDOW EDGE. Radix's own default is
      // `collisionPadding: 0`, which does not fail loudly — it CLAMPS, so an
      // overflowing menu lands with its right border exactly on `innerWidth`
      // and the reader sees a panel sheared off by the viewport rather than a
      // panel that moved. Measured on `/insights` at 1440x900, 2026-08-27:
      // the scope menu rendered `left 1152 → right 1440.0` against a 1440px
      // viewport — zero gutter, rounded corner and elevation shadow both cut.
      // Founder: "the all channels menu is going off the screen to the right,
      // should not be doing that."
      //
      // `12` is not a new number: `DropdownMenuContent` and
      // `DropdownMenuSubContent` have defaulted to `collisionPadding ?? 12`
      // for exactly this reason (`dropdown-menu.tsx:59`), and the two
      // primitives sitting on different defaults is why the same overflow was
      // impossible in a dropdown and shipped in a popover. Callers may still
      // pass their own; this only supplies the floor.
      collisionPadding={collisionPadding ?? 12}
      className={cn(
        'z-popover max-h-[var(--radix-popover-content-available-height)] w-72 overflow-y-auto overscroll-contain rounded-xl border border-border/60 bg-popover/95 p-4 text-popover-foreground shadow-elevation-3 outline-none backdrop-blur-xl origin-(--radix-popover-content-transform-origin)',
        OVERLAY_POP_MOTION,
        className,
      )}
      {...props}
    />
  </PopoverContentPortal>
));
PopoverContent.displayName = 'PopoverContent';

function PopoverContentPortal({
  container,
  children,
}: {
  container?: HTMLElement | null;
  children: React.ReactNode;
}): JSX.Element {
  const dialogContainer = useDialogContentContainer();
  return (
    <PopoverPrimitive.Portal container={container ?? dialogContainer}>
      {children}
    </PopoverPrimitive.Portal>
  );
}

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
